import React, { useState, useEffect } from "react";
import { Link } from "gatsby";
import PropTypes from "prop-types";
import Card from "~/components/styled/card";
import Image from "~/components/image";
import { formatPrice } from "~/helpers/currency-formatter";
import TooltipLodging from "~/components/styled/TooltipLodging";
import { ChevronRightIcon } from "@heroicons/react/outline";
//import ListSlider from "~/components/styled/ListSlider";
import GalleryTrigger from "~/components/GalleryTrigger";

const TourList = ({ tours, gridCols }) => {
	const [list, setList] = useState([...tours.slice(0, 6)]);
	const [loadMore, setLoadMore] = useState(false);
	const [hasMore, setHasMore] = useState(tours.length > 6);

	const handleLoadMore = () => {
		setLoadMore(true);
	};

	useEffect(() => {
		if (loadMore && hasMore) {
			const currentLength = list.length;
			const isMore = currentLength < tours.length;
			const nextResults = isMore
				? tours.slice(currentLength, currentLength + 6)
				: [];
			setList([...list, ...nextResults]);
			setLoadMore(false);
		}
	}, [loadMore, hasMore]);

	useEffect(() => {
		const isMore = list.length < tours.length;
		setHasMore(isMore);
	}, [list]);

	return (
		<div className={`grid ${gridCols} gap-8`}>
			{list.map((tour, index) => (
				<div className="relative mb-12" key={tour.id}>
					<Card className="transition-all duration-300 transform group-hover:-translate-y-1 group-hover:shadow-lg cursor-pointer">
						{/* Image wrapper for zoom effect */}
						{/* {(tour.slider ?? []).some(
							(s) => s?.localFile?.childImageSharp?.gatsbyImageData
						) ? (
							<ListSlider slides={tour.slider} />
						) : (
							<div className="overflow-hidden rounded-t-md border-b border-gray-200">
								<Image
									alt={tour.title || "Tour Image"}
									className="transform transition-transform duration-300 group-hover:scale-105"
									loading={index === 0 ? "eager" : "lazy"}
									image={tour.featuredImage}
								/>
							</div>
						)} */}

						{/* Single image → click opens lightbox (falls back to featuredImage) */}
						<GalleryTrigger tour={tour} eager={index === 0} />

						{/* Badges */}
						{tour.popular === true && (
							<div className="absolute top-3 left-3 bg-getaway rounded-full px-3 py-1">
								<p className="text-black text-xs font-medium capitalize">Popular</p>
							</div>
						)}
						{tour.new === true && (
							<div className="absolute top-3 right-3 bg-new rounded-full px-3 py-1">
								<p className="text-white text-xs font-medium capitalize">New</p>
							</div>
						)}
						{/* Body */}
						<Link
							to={`/tours/${tour.slug}`}
							aria-label={`View ${tour.title}`}
							className="block group"
						>
							{/* Tour type + activity */}
							{(tour.tourType || tour.activity) && (
								<div className="flex items-center gap-2 mt-3 ml-3">
									{tour.tourType && (
										<span className="text-xs font-medium uppercase rounded-md border bg-primary text-white px-2 py-0.5">
											{tour.tourType === "selfguided" ? "Self-guided" : "Guided"}
										</span>
									)}
									{tour.activity && (
										<span className="text-xs font-medium uppercase rounded-md border bg-gray-100 px-2 py-0.5">
											{tour.activity.name}
										</span>
									)}
								</div>
							)}
							<div className="px-3 py-3">
								{/* Title */}
								<h3 className="normal-case font-semibold text-lg transition-colors duration-300 group-hover:text-primary line-clamp-2">
									{tour.title}
								</h3>

								{/* Regions */}
								{tour.regions?.length > 0 && (
									<div className="flex flex-wrap gap-1 mt-1">
										{tour.regions.map((region) => (
											<p
												key={`region-${region.id}`}
												className="text-xs text-gray-600 border rounded-full p-0.5 px-2"
											>
												{region.name}
											</p>
										))}
									</div>
								)}

								{/* Quick facts */}
								{(tour?.quickfacts?.lodging || tour?.quickfacts?.level) && (
									<div className="flex flex-col capitalize gap-0 text-sm text-gray-600 mt-4">
										{tour?.quickfacts?.lodging && (
											<span className="inline-flex items-center">
												{tour.quickfacts.lodging === "casual_plus" ? (
													<>
														casual<span className="text-yellow-600 font-semibold">+ </span>
													</>
												) : (
													tour.quickfacts.lodging
												)}
												&nbsp;hotels <TooltipLodging />
											</span>
										)}

										{tour?.quickfacts?.level && (
											<span className="">
												{tour.quickfacts.level.split("_").join(" to ")} level
											</span>
										)}
									</div>
								)}

								{/* Duration + Price */}
								<div className="mt-2">
									<p className="inline-flex items-baseline gap-1 text-base font-semibold">
										<span className="capitalize">{tour.duration}</span>
										<span className="text-sm text-gray-500 font-normal">from</span>
										{tour.price ? (
											<>
												<data value={tour.price}>{formatPrice(tour.price)}</data>
												<span className="text-xs font-normal text-gray-500">/person</span>
											</>
										) : (
											<span className="text-sm font-normal text-gray-500">
												Price on request
											</span>
										)}
									</p>
								</div>

								{/* CTA (visual only, whole card clickable) */}
								<div className="bg-primary2 transition-colors duration-300 group-hover:bg-primary font-medium tracking-wide text-sm mt-3 text-white w-full py-2 rounded-md text-center">
									View Details
								</div>
							</div>
						</Link>
					</Card>
				</div>
			))}

			{/* Load more */}
			<div className="items-start -mt-6 mb-10">
				{/* Load more */}
				{hasMore && (
					<div className="items-start -mt-6 mb-10">
						<button
							onClick={handleLoadMore}
							className="w-auto h-full py-3 px-6 md:py-4 md:px-8 font-medium bg-white border-2 border-black rounded-xl"
						>
							Show more
							<ChevronRightIcon className="w-5 h-5 inline-flex ml-1" />
						</button>
					</div>
				)}
			</div>
		</div>
	);
};

TourList.propTypes = {
	tours: PropTypes.array,
	gridCols: PropTypes.string,
};

TourList.defaultProps = {
	gridCols: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
};

export default TourList;
